import { Box, Theme, Typography } from '@mui/material';
import { uniq } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { OrderItemStatus as OrderItemStatusType, SelfOrderPoint } from '../../apiTypes';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import useMedia from '../../utils/useMedia';
import DeliveryOrdersList from './Delivery/DeliveryOrdersList';

type Props = {
  point: DbDocument<SelfOrderPoint>;
};

const Point = ({ point }: Props) => {
  const navigate = useNavigate();
  const { isMobile } = useMedia();
  const { cafeSlug } = useAdmin();
  const { orders, reservations } = useCafe();

  const targetOrders = useMemo(() => orders.filter((o) => o.selfOrderPointId === point.id), [orders, point.id]);

  const itemStatuses = useMemo(
    () =>
      uniq(
        targetOrders.reduce<OrderItemStatusType[]>(
          (acc, o) => [...acc, ...Object.values(o.items).map((i) => i.status)],
          []
        )
      ),
    [targetOrders]
  );

  const hasUnconfirmedOrder = useMemo(() => targetOrders.some((o) => o.status === 'ordered'), [targetOrders]);

  const getColor = useCallback(
    (t: Theme) => {
      if (hasUnconfirmedOrder) {
        return { main: t.palette.error.light, border: t.palette.error.main };
      }

      if (targetOrders.length === 0) {
        return { main: t.palette.grey[500], border: t.palette.grey[700] };
      }

      if (itemStatuses.includes('ready')) {
        return { main: t.palette.error.light, border: t.palette.error.main };
      }

      if (itemStatuses.length === 1 && itemStatuses[0] === 'complete') {
        return { main: t.palette.success.light, border: t.palette.success.main };
      }

      if (itemStatuses.includes('ordered') || itemStatuses.includes('processing')) {
        return { main: t.palette.secondary.main, border: t.palette.secondary.dark };
      }

      return { main: t.palette.secondary.main, border: t.palette.secondary.dark };
    },
    [hasUnconfirmedOrder, targetOrders.length, itemStatuses]
  );

  return (
    <Box
      id={`point-${point.id}`}
      onClick={() => navigate(`/${cafeSlug}/pos/orders/?point=${point.id}`)}
      sx={{
        backgroundColor: (t) => getColor(t).main,
        border: (t) => `2px solid ${getColor(t).border}`,
        width: isMobile ? '100%' : '25%',
        height: 100,
        borderRadius: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        userSelect: 'none',
        cursor: 'pointer',
      }}
    >
      <Typography variant={isMobile ? 'body2' : 'body1'} color="white" textAlign="center">
        {point.name}
      </Typography>
    </Box>
  );
};

const PoinsList = () => {
  const { isMobile } = useMedia();
  const { deliveryOrders, selfOrderPoints } = useCafe();

  return (
    <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
      <DeliveryOrdersList deliveryOrders={deliveryOrders} hasAddButton />
      <Box sx={{ width: '100%', display: 'flex', flexWrap: 'wrap', gap: isMobile ? 2 : 3 }}>
        {selfOrderPoints.map((point) => (
          <Point key={point.id} point={point} />
        ))}
      </Box>
    </Box>
  );
};

export default PoinsList;
